// @flow
import React from "react"
import { type HOC, compose, withHandlers } from "recompose"
import withOpen from "hoc/withOpen"
import withQuery from "hoc/withQuery"
import withConnect from "hoc/withConnect"
import { affiliateSupportedProductGroupsQuery } from "data/affiliate/graphql"
import { selectAllProductGroups, selectProductGroups } from "data/app/selectors"
import { selectActivity } from "data/app/selectors"
import type { ProductGroup } from "data/app/types"
import { closeModal } from "data/modals/actions"
import { selectSearchParams } from "data/search/selectors"
import { MultiselectItem } from "searchFilters/Shared/Multiselect/styled"
import Checkbox from "components/Checkbox"
import { Text } from "componentsStyled/Typography/Texts"

type Props = {|
  /** All product groups as defined in the backend */
  allProductGroups: ProductGroup[],
  /** Selected product groups */
  value: ProductGroup[],
  /** Callback to set selected product groups */
  onChange: ProductGroup[] => mixed,
  /** Product groups supported by the affiliate (if on affiliate page) */
  data: ?(ProductGroup[]),
|}

/**
 * Renders list of selectable product groups appropriate for the page, i.e.
 * main search page shows all groups and affiliate page shows one supported by
 * the affiliate.
 */
const ProductGroupFilter = ({
  allProductGroups,
  value,
  handleChooseProductGroup,
  data,
}) => {
  /** Filter out ones not supported by the affiliate */
  const selectableProductGroups = allProductGroups.filter((productGroup: ProductGroup) => {
    if (!data) {
      return true;
    }
    return data.some(pg => pg.name === productGroup.name)
  })

  return (
    <React.Fragment>
      {selectableProductGroups.map(selectableProductGroup => {
        const active = value && value.map(v => v.id).includes(selectableProductGroup.id)
        return (
          <MultiselectItem key={selectableProductGroup.id} onClick={() => handleChooseProductGroup(selectableProductGroup)}>
            <Text book={active} black={active}>
              {selectableProductGroup.name}
            </Text>{" "}
            <Checkbox value={active} />
          </MultiselectItem>
        )
      })}
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  activity: selectActivity(state),
  allProductGroups: selectAllProductGroups(state),
  productGroups: selectProductGroups(state),
  params: selectSearchParams(state),
})

const mapDispatchToProps = {
  closeModal,
}

const enhancer: HOC<*, Props> = compose(
  withConnect(mapStateToProps, mapDispatchToProps),
  withQuery(affiliateSupportedProductGroupsQuery, {
    variables: props => ({
      id: props.params.affiliate,
    }),
    noEmpty: true,
    config: {
      skip: props => !props.params.affiliate,
    }
  }),
  withOpen,
  withHandlers({
    handleChooseProductGroup: props => (selectedProductGroup: ProductGroup) => {
      const prevValue = props.value || []

      if (prevValue.map(x => x.id).includes(selectedProductGroup.id)) {
        const newValue = prevValue.filter(x => x.id !== selectedProductGroup.id)
        return props.onChange(newValue)
      }

      return props.onChange(prevValue.concat(selectedProductGroup))
    }
  }),
)

export default enhancer(ProductGroupFilter)



// WEBPACK FOOTER //
// ./src/searchFilters/FilterComponents/ProductGroupFilter/index.js